import { useEffect, useMemo, useState } from 'react';
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  Button,
  Search,
  Checkbox,
  InlineLoading,
  Tabs,
  TabList,
  Tab,
  TextArea,
} from '@carbon/react';
import classNames from 'classnames';
import { DEFAULT_COLORS } from 'components/color-picker';
import type {
  AttributeModalMode,
  ColorByApplyTarget,
  ColorByAttributeModalProps,
  ColorByCategory,
  ColorByCategoryGroup,
  ColorByOption,
  ColorByRule,
} from './types';
import styles from './color-by-attribute-modal.module.scss';

// Resolve a value's color: assigned color wins, else palette entry at index.
function getValueColor(
  value: string,
  index: number,
  existingColors: Record<string, string>,
  palette: string[]
): string {
  return existingColors[value] ?? palette[index % palette.length];
}

// Tab bar (tabbed mode): one tab per category group, with the active group's
// "applies to" note right-aligned.
const GroupTabs = ({
  groups,
  activeGroupId,
  onSelect,
}: {
  groups: ColorByCategoryGroup[];
  activeGroupId: string;
  onSelect: (id: string) => void;
}) => {
  const activeIndex = Math.max(
    0,
    groups.findIndex((g) => g.id === activeGroupId)
  );
  const active = groups[activeIndex];
  return (
    <div className={styles.tabRow}>
      <Tabs
        selectedIndex={activeIndex}
        onChange={({ selectedIndex }) => {
          const group = groups[selectedIndex];
          if (group) onSelect(group.id);
        }}
      >
        <TabList aria-label="Attribute groups">
          {groups.map((group) => (
            <Tab key={group.id}>{group.label}</Tab>
          ))}
        </TabList>
      </Tabs>
      {active?.appliesToLabel && (
        <span className={styles.appliesTo}>{active.appliesToLabel}</span>
      )}
    </div>
  );
};

// Searchable list of attributes (left column).
const CategoryList = ({
  categories,
  activeCategory,
  onSelect,
  searchPlaceholder,
}: {
  categories: ColorByCategory[];
  activeCategory: string;
  onSelect: (id: string) => void;
  searchPlaceholder: string;
}) => {
  const [search, setSearch] = useState('');
  const filtered = categories.filter((cat) =>
    cat.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className={styles.categoriesInner}>
      <div className={styles.categoriesSearch}>
        <Search
          labelText="Search filters"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <ul className={styles.categoryList}>
        {filtered.map((cat) => (
          <li key={cat.id} className={styles.categoryItem}>
            <button
              type="button"
              onClick={() => onSelect(cat.id)}
              className={classNames(styles.categoryButton, {
                [styles.categoryButtonActive]: activeCategory === cat.id,
              })}
            >
              {cat.label}
            </button>
          </li>
        ))}
        {filtered.length === 0 && (
          <li className={styles.categoryItem}>
            <p className={styles.noResults}>No attributes</p>
          </li>
        )}
      </ul>
    </div>
  );
};

// Value picker: selected-value preview + searchable checkbox list with
// select-all/clear. In tabbed mode it mirrors the mock (subtitle, "Selected
// values" label, attribute name above the search).
const AttributeForm = ({
  category,
  options,
  selectedValues,
  onChange,
  existingValueColors,
  palette,
  tabbed,
  mode,
  showPasteList,
}: {
  category: ColorByCategory | undefined;
  options: ColorByOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  existingValueColors: Record<string, string>;
  palette: string[];
  tabbed: boolean;
  mode: AttributeModalMode;
  showPasteList: boolean;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  // Bulk entry. Searching and ticking works for a handful of values; a reader arriving
  // with a list of operators or UWIs from elsewhere would otherwise tick them one at a
  // time. Closed until asked for, so the common case is unchanged.
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  if (!category) return null;

  const filteredOptions = options.filter((opt) =>
    opt.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredIds = filteredOptions.map((o) => o.id);
  // Selected values are stored by id (the match key); show the display name
  // (e.g. landing-zone code -> geologic interval), falling back to the id.
  const optionNameById = Object.fromEntries(options.map((o) => [o.id, o.name]));
  const allSelected =
    filteredIds.length > 0 &&
    filteredIds.every((id) => selectedValues.includes(id));

  const handleSelectAll = () => {
    if (allSelected) {
      onChange(selectedValues.filter((v) => !filteredIds.includes(v)));
    } else {
      onChange([...new Set([...selectedValues, ...filteredIds])]);
    }
  };

  // Matches on the id or the display name, case-insensitively, because a pasted list
  // comes from a spreadsheet or another screen and carries whichever the reader saw.
  // Adds to the selection rather than replacing it, so pasting twice accumulates.
  const handleSelectMatches = () => {
    const wanted = new Set(
      pasteText
        .split(/[\n,]/)
        .map((entry) => entry.trim().toLowerCase())
        .filter(Boolean)
    );
    if (wanted.size === 0) return;

    const matched = options
      .filter(
        (opt) =>
          wanted.has(opt.id.toLowerCase()) || wanted.has(opt.name.toLowerCase())
      )
      .map((opt) => opt.id);

    onChange([...new Set([...selectedValues, ...matched])]);
  };

  return (
    <div className={styles.formWrapper}>
      <h4
        className={classNames(styles.formTitle, {
          [styles.formTitleFlush]: tabbed,
        })}
      >
        {category.label}
      </h4>
      {tabbed && (
        <p className={styles.formSubtitle}>
          Select the values to color and choose where it applies.
        </p>
      )}
      <div className={styles.distinctColors}>
        <span className={styles.distinctColorsLabel}>
          {mode === 'filter'
            ? 'Selected Filters'
            : tabbed
              ? 'Selected values'
              : 'Distinct Colors'}
        </span>
        {selectedValues.length === 0 ? (
          <span className={styles.distinctColorsEmpty}>
            {mode === 'filter' ? 'No filters selected' : 'No values selected'}
          </span>
        ) : (
          <div className={styles.distinctColorsList}>
            {selectedValues.map((val, idx) => (
              <div key={val} className={styles.distinctColorRow}>
                {mode === 'color' && (
                  <span
                    className={styles.distinctColorSwatch}
                    style={{
                      backgroundColor: getValueColor(
                        val,
                        idx,
                        existingValueColors,
                        palette
                      ),
                    }}
                  />
                )}
                <span className={styles.distinctColorName}>
                  {optionNameById[val] ?? val}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className={styles.formContent}>
        {tabbed && (
          <span className={styles.valueHeading}>{category.label}</span>
        )}
        <Search
          labelText={`Search ${category.label.toLowerCase()}`}
          placeholder={`Search ${category.label.toLowerCase()}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="sm"
        />
        <div className={styles.selectRow}>
          <button
            type="button"
            className={styles.selectAllLink}
            onClick={handleSelectAll}
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
          {showPasteList && (
            <button
              type="button"
              className={styles.selectAllLink}
              aria-expanded={pasteOpen}
              onClick={() => setPasteOpen((prev) => !prev)}
            >
              Paste list
            </button>
          )}
          <span className={styles.resultCount}>
            Top {filteredOptions.length} results shown
          </span>
        </div>
        {showPasteList && pasteOpen && (
          <div className={styles.pastePanel}>
            <TextArea
              id={`color-by-paste-${category.id}`}
              labelText={`Paste ${category.label} values`}
              helperText="One per line (or separated by commas). Matching options are auto-selected."
              rows={4}
              value={pasteText}
              onChange={(event) => setPasteText(event.target.value)}
              // Real values from this attribute, so the reader can see the form their
              // list should take rather than guessing from a generic example.
              placeholder={
                options.length
                  ? `e.g.\n${options
                      .slice(0, 3)
                      .map((opt) => opt.name)
                      .join('\n')}`
                  : undefined
              }
            />
            <div className={styles.pasteActions}>
              <button
                type="button"
                className={styles.selectAllLink}
                onClick={handleSelectMatches}
              >
                Select matches
              </button>
              <button
                type="button"
                className={styles.selectAllLink}
                onClick={() => setPasteText('')}
              >
                Clear
              </button>
            </div>
          </div>
        )}
        <div className={styles.optionsList}>
          {filteredOptions.map((option) => (
            <Checkbox
              key={option.id}
              id={`color-by-${category.id}-${option.id}`}
              labelText={option.name}
              checked={selectedValues.includes(option.id)}
              onChange={(_evt, { checked }) => {
                onChange(
                  checked
                    ? [...selectedValues, option.id]
                    : selectedValues.filter((v) => v !== option.id)
                );
              }}
            />
          ))}
          {filteredOptions.length === 0 && (
            <p className={styles.noResults}>No options available</p>
          )}
        </div>
      </div>
      <div className={styles.formFooter}>
        <button
          type="button"
          className={styles.clearLink}
          onClick={() => onChange([])}
          disabled={selectedValues.length === 0}
        >
          Clear
        </button>
      </div>
    </div>
  );
};

// Apply-To panel: a checkbox per surface the rule can paint; surfaces the
// consumer marks unavailable for the active attribute render disabled + off.
const ApplyToPanel = ({
  targets,
  applyTo,
  resetApplyTo,
  onChange,
  activeCategory,
  isTargetDisabled,
  tabbed,
}: {
  targets: ColorByApplyTarget[];
  applyTo: Record<string, boolean>;
  resetApplyTo: Record<string, boolean>;
  onChange: (applyTo: Record<string, boolean>) => void;
  activeCategory: string;
  isTargetDisabled?: (attributeId: string, targetId: string) => boolean;
  tabbed: boolean;
}) => {
  const hasAnyApplyTo = Object.values(applyTo).some(Boolean);

  return (
    <div className={styles.applyToPanel}>
      <h4 className={styles.applyToTitle}>Apply To</h4>
      <div className={styles.applyToContent}>
        {tabbed && (
          <p className={styles.applyToSubtitle}>
            Choose which surfaces this color-by colors.
          </p>
        )}
        {/* Explains the greying before the reader meets it, rather than only under each
            struck-out target. Shown whenever a target can be disabled at all, since that
            is the only situation in which the rule applies. */}
        {isTargetDisabled && targets.length > 0 && (
          <p className={styles.applyToSubtitle}>
            Disabled targets are already assigned to another active color-by.
          </p>
        )}
        {/* Every surface is spoken for, so there is no rule to be made here until one is
            freed. Saying which way out exists beats an all-grey list and a dead Apply. */}
        {targets.length > 0 &&
          targets.every(({ id }) => isTargetDisabled?.(activeCategory, id)) && (
            <p className={styles.applyToEmpty}>
              No targets available. Remove or retarget an existing color-by
              rule.
            </p>
          )}
        <div className={styles.applyToList}>
          {targets.map(({ id, label }) => {
            const disabled = Boolean(isTargetDisabled?.(activeCategory, id));
            return (
              <div key={id} className={styles.applyToItem}>
                <Checkbox
                  id={`apply-to-${id}`}
                  labelText={label}
                  disabled={disabled}
                  checked={!disabled && Boolean(applyTo[id])}
                  onChange={(_evt, { checked }) => {
                    onChange({ ...applyTo, [id]: checked });
                  }}
                />
                {/* A greyed-out surface with no reason reads as a bug. Say who holds it,
                    so the way out — disable or remove that rule — is obvious. */}
                {disabled && (
                  <p className={styles.applyToTaken}>
                    Already assigned by another active color-by rule
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.formFooter}>
        <button
          type="button"
          className={styles.clearLink}
          onClick={() => onChange({ ...resetApplyTo })}
          disabled={!hasAnyApplyTo}
        >
          Clear
        </button>
      </div>
    </div>
  );
};

// Generic "Color by Attribute" modal. Owns editing UI + rule composition;
// attributes, options, surfaces, side fetch injected by the consumer. Pass
// `categoryGroups` for the tabbed (Common / Wells / Documents) layout.
export const ColorByAttributeModal = ({
  open,
  onClose,
  editingRuleId,
  rules,
  onApply,
  categories,
  categoryGroups,
  getOptions,
  applyToTargets = [],
  newRuleApplyTo,
  resetApplyTo = {},
  onSelectionChange,
  isTargetDisabled,
  isFetching = false,
  colorPalette = DEFAULT_COLORS,
  title = 'Color by Attribute',
  showReset = true,
  showApplyTo = true,
  showPasteList = false,
  requireApplyTarget = false,
  // Loading row text; generic default since the modal is shared across datasets.
  loadingDescription = 'Loading…',
  mode = 'color',
  initialGroupId,
  selectorsFloatingMenus,
  categorySearchPlaceholder = 'Search Filter Names',
}: ColorByAttributeModalProps) => {
  const editingRule = useMemo(() => {
    if (!editingRuleId) return null;
    return rules.find((r) => r.ruleId === editingRuleId) ?? null;
  }, [editingRuleId, rules]);

  const tabbed = !!categoryGroups?.length;
  const groups = useMemo(() => categoryGroups ?? [], [categoryGroups]);

  const [activeGroupId, setActiveGroupId] = useState<string>(
    groups[0]?.id ?? ''
  );
  const [activeCategory, setActiveCategory] = useState<string>(
    tabbed ? (groups[0]?.categories[0]?.id ?? '') : (categories?.[0]?.id ?? '')
  );
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [applyTo, setApplyTo] = useState<Record<string, boolean>>({
    ...newRuleApplyTo,
  });

  const activeGroup = groups.find((g) => g.id === activeGroupId) ?? groups[0];
  // A consumer-rendered tab replaces the value picker, Apply To and the rule write.
  const activePanel = tabbed ? activeGroup?.panel : undefined;
  const effectiveCategories = tabbed
    ? (activeGroup?.categories ?? [])
    : (categories ?? []);

  // Seed local edit state when the modal opens (edit vs create).
  useEffect(() => {
    if (!open) return;
    if (editingRule) {
      if (tabbed) {
        const gid =
          editingRule.groupId ??
          groups.find((g) =>
            g.categories.some((c) => c.id === editingRule.attribute)
          )?.id ??
          groups[0]?.id ??
          '';
        setActiveGroupId(gid);
      }
      setActiveCategory(editingRule.attribute);
      setSelectedValues([...editingRule.selectedValues]);
      setApplyTo({ ...editingRule.applyTo });
    } else {
      if (tabbed) {
        const start = groups.find((g) => g.id === initialGroupId) ?? groups[0];
        setActiveGroupId(start?.id ?? '');
        setActiveCategory(start?.categories[0]?.id ?? '');
      } else {
        setActiveCategory(categories?.[0]?.id ?? '');
      }
      setSelectedValues([]);
      setApplyTo({ ...newRuleApplyTo });
    }
    // newRuleApplyTo/categories/groups are stable consumer config.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingRule]);

  // Notify the consumer so it can run any side fetch for the selection.
  useEffect(() => {
    if (!open) return;
    onSelectionChange?.(activeCategory, selectedValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeCategory, selectedValues]);

  const selectGroup = (groupId: string) => {
    setActiveGroupId(groupId);
    const first = groups.find((g) => g.id === groupId)?.categories[0]?.id ?? '';
    setActiveCategory(first);
    // Switching tabs lands on a different attribute, so drop the staged values
    // unless we're back on the rule being edited (its own attribute).
    if (!editingRule || editingRule.attribute !== first) setSelectedValues([]);
  };

  const optionsGroupId = tabbed ? activeGroupId : undefined;

  const handleApply = () => {
    if (isFetching) return;
    if (activePanel) {
      activePanel.onApply();
      onClose();
      return;
    }

    const existingValueColors = editingRule?.valueColors ?? {};
    const valueColors: Record<string, string> = { ...existingValueColors };
    selectedValues.forEach((val, idx) => {
      if (!valueColors[val]) {
        valueColors[val] = colorPalette[idx % colorPalette.length];
      }
    });
    // Drop colors for values no longer selected.
    for (const key of Object.keys(valueColors)) {
      if (!selectedValues.includes(key)) delete valueColors[key];
    }

    // Display labels aligned index-for-index with selectedValues, stored on the rule so
    // the legend renders names after reload without a facets fetch. Resolved in order:
    // live option name, then the label already saved, then the value id.
    const options = getOptions(activeCategory, optionsGroupId);
    const priorLabelByValue: Record<string, string> = Object.fromEntries(
      (editingRule?.selectedValues ?? []).flatMap((val, idx) => {
        const label = editingRule?.selectedValueLabels?.[idx];
        return label === undefined ? [] : [[val, label]];
      })
    );
    const selectedValueLabels = selectedValues.map(
      (val) =>
        options.find((o) => o.id === val)?.name ?? priorLabelByValue[val] ?? val
    );

    // Drop any surface that's unavailable for the chosen attribute.
    const resolvedApplyTo = Object.fromEntries(
      Object.entries(applyTo).map(([key, on]) => [
        key,
        on && !isTargetDisabled?.(activeCategory, key),
      ])
    );

    const newRule: ColorByRule = {
      ruleId:
        editingRule?.ruleId ??
        `${activeCategory}-${Date.now()}-${Math.round(Math.random() * 1000)}`,
      attribute: activeCategory,
      selectedValues,
      selectedValueLabels,
      valueColors,
      applyTo: resolvedApplyTo,
      enabled: editingRule?.enabled ?? true,
      // In tabbed mode the rule follows the active tab, so moving a rule to
      // another tab re-scopes it; flat mode keeps any existing group id.
      groupId: tabbed ? activeGroupId : editingRule?.groupId,
    };

    const nextRules = editingRule
      ? rules.map((r) => (r.ruleId === editingRule.ruleId ? newRule : r))
      : [...rules, newRule];

    onApply(nextRules);
    onClose();
  };

  const handleReset = () => {
    setSelectedValues([]);
    setApplyTo({ ...resetApplyTo });
  };

  const activeCategoryObj = effectiveCategories.find(
    (c) => c.id === activeCategory
  );
  const applyLabel = mode === 'filter' ? 'Apply Filter' : 'Apply';

  // Every surface this rule could paint is unticked or held by another rule, so
  // applying would file a rule that colours nothing. Opt-in, because the Cross-Section,
  // Pre-Drill and Area rails ship without this gate and must keep their Apply working.
  const paintsNothing =
    !activePanel &&
    requireApplyTarget &&
    showApplyTo &&
    mode === 'color' &&
    applyToTargets.length > 0 &&
    !applyToTargets.some(
      ({ id }) => applyTo[id] && !isTargetDisabled?.(activeCategory, id)
    );

  return (
    <ComposedModal
      open={open}
      onClose={onClose}
      size="lg"
      className={classNames(styles.modal, { [styles.tabbed]: tabbed })}
      selectorsFloatingMenus={selectorsFloatingMenus}
    >
      <ModalHeader title={title} />
      <ModalBody>
        {tabbed && (
          <GroupTabs
            groups={groups}
            activeGroupId={activeGroupId}
            onSelect={selectGroup}
          />
        )}
        <div className={styles.content}>
          <div className={styles.categories}>
            <CategoryList
              key={tabbed ? activeGroupId : 'flat'}
              categories={effectiveCategories}
              activeCategory={activeCategory}
              searchPlaceholder={categorySearchPlaceholder}
              onSelect={(id) => {
                setActiveCategory(id);
                if (!editingRule || editingRule.attribute !== id) {
                  setSelectedValues([]);
                }
              }}
            />
          </div>
          {activePanel ? (
            <div className={styles.form}>{activePanel.content}</div>
          ) : (
            <div className={styles.form}>
              <AttributeForm
                key={activeCategory}
                category={activeCategoryObj}
                options={getOptions(activeCategory, optionsGroupId)}
                selectedValues={selectedValues}
                onChange={setSelectedValues}
                existingValueColors={editingRule?.valueColors ?? {}}
                palette={colorPalette}
                tabbed={tabbed}
                mode={mode}
                showPasteList={showPasteList}
              />
              {isFetching && <InlineLoading description={loadingDescription} />}
            </div>
          )}
          {!activePanel && showApplyTo && mode === 'color' && (
            <div className={styles.applyTo}>
              <ApplyToPanel
                targets={applyToTargets}
                applyTo={applyTo}
                resetApplyTo={resetApplyTo}
                onChange={setApplyTo}
                activeCategory={activeCategory}
                isTargetDisabled={isTargetDisabled}
                tabbed={tabbed}
              />
            </div>
          )}
        </div>
      </ModalBody>
      <div
        className={classNames(styles.customFooter, {
          [styles.customFooterTwo]: !showReset,
        })}
      >
        <Button
          size="lg"
          kind={showReset ? 'ghost' : 'secondary'}
          className={
            showReset ? styles.footerButton : styles.footerButtonCancel
          }
          onClick={onClose}
          disabled={isFetching}
        >
          Cancel
        </Button>
        {showReset && (
          <Button
            size="lg"
            kind="secondary"
            className={styles.footerButton}
            onClick={handleReset}
          >
            Reset
          </Button>
        )}
        <Button
          size="lg"
          kind="primary"
          className={showReset ? styles.footerButton : styles.footerButtonApply}
          onClick={handleApply}
          disabled={isFetching || paintsNothing}
        >
          {isFetching ? 'Loading...' : applyLabel}
        </Button>
      </div>
    </ComposedModal>
  );
};

ColorByAttributeModal.displayName = 'ColorByAttributeModal';
